import { ConsoleSqlOutlined, SwapOutlined } from '@ant-design/icons';
import { faCircleDot, faEllipsisV, faLocationCrosshairs, faMapMarkerAlt, faSearch, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Divider, Flex, Form, Input, Select, Spin } from 'antd';
import { forwardRef, useEffect, useState } from 'react';
import './index.less';
import { isNil } from 'lodash';
import useIsMobile from '../../helpers/customHooks/useIsMobile';
import { getNearestSearchOptionIndex, isNearestSearchOption } from '../../constants/NearestSearchOptions';
import { isCurrentLocationOption } from '../../constants/CurrentLocationOption';

const { Item } = Form;

// const travel_mode = [
// 	{
// 		value: 'walk',
// 		label: <><FontAwesomeIcon
// 			style={{ fontSize: 16 }}
// 			icon={faWalking}
// 		/></>,
// 		key: 'wk'
// 	},
// 	{
// 		value: 'bike',
// 		label: <><FontAwesomeIcon
// 			style={{ fontSize: 16 }}
// 			icon={faBiking}
// 		/></>,
// 		key: 'bk'
// 	},
// 	{
// 		value: 'wheelchair',
// 		label: <><FontAwesomeIcon
// 			style={{ fontSize: 16 }}
// 			icon={faWheelchair}
// 		/></>,
// 		key: 'wchr'
// 	}
// ];

const MapDirectionsForm = forwardRef(
	function ({ data, onClose, onSwap, onClick, onSelect, onChange, onKeyDown, onClearField, onKeyUp, options, loading, focusedField, startEnd }, ref) {
		// const [isMorethanOneDestination, setIsMorethanOneDestination] = useState(false);
		const handleOnSwap = () => onSwap();
		const isMobile = useIsMobile();

		// Where an entry sits above the real locations: Current Location first, then the nearest
		// searches in their declared order. -1 for anything that is a real location.
		const specialOptionRank = value => {
			if (isCurrentLocationOption(value)) return 0;
			const index = getNearestSearchOptionIndex(value);
			return index >= 0 ? index + 1 : -1;
		};

		const hadleFilterSelect = (optionA, optionB, info) => {
			const searchValue = info?.input?.toLowerCase() || '';
			const rankA = specialOptionRank(optionA.value);
			const rankB = specialOptionRank(optionB.value);

			// Special entries keep their declared order between themselves...
			if (rankA >= 0 && rankB >= 0) {
				return rankA - rankB;
			}

			// ...and stay pinned above the real locations
			if (rankA >= 0 && (optionA.search_key?.toLowerCase() ?? '').includes(searchValue)) {
				return -1;
			}
			if (rankB >= 0 && (optionB.search_key?.toLowerCase() ?? '').includes(searchValue)) {
				return 1;
			}

			return (optionA?.search_key ?? '').toLowerCase().localeCompare((optionB?.search_key ?? '').toLowerCase());
		};

		const customFilterOption = (input, option) => {
			const searchValue = input.toLowerCase().trim();
			// Current Location is the one entry worth offering before anything has been typed:
			// routing from where you are standing is the common case, not a search.
			if (isCurrentLocationOption(option.value)) {
				return searchValue === '' || (option.search_key?.toLowerCase() ?? '').includes(searchValue);
			}
			if (isNearestSearchOption(option.value)) {
				return searchValue !== '' && (option.search_key?.toLowerCase() ?? '').includes(searchValue);
			}
			return (option.search_key?.toLowerCase() ?? '').includes(searchValue);
		};

		// "Nearest lift to the nearest toilet" has no starting point, and a route from here to
		// here is not a route, so each special entry may occupy only one of the two fields.
		const optionsForField = counterpart => options?.filter(option => {
			if (isNearestSearchOption(option.value)) return !isNearestSearchOption(counterpart);
			if (isCurrentLocationOption(option.value)) return !isCurrentLocationOption(counterpart);
			return true;
		});
		const startOptions = optionsForField(startEnd.end);
		const endOptions = optionsForField(startEnd.start);

		// NOTE what a field displays is whatever antd has stored in the Form, and nothing else:
		// Form.Item clones its child and injects `value`, and cloneElement lets the injected prop
		// win, so a `value` declared on the Select here is silently ignored. Picking an entry works
		// because antd writes it to the form itself; anything set behind the form's back has to be
		// pushed in with setFieldsValue (see DirectionsPage).

		const renderOption = option => (
			isCurrentLocationOption(option.value)
				? <Flex
					align="center"
					gap={8}
				  >
					<FontAwesomeIcon
						icon={faLocationCrosshairs}
						color="#1A73E8"
					/>
					{option.label}
				</Flex>
				: option.label
		);

		return (
			<>
				<div className="hkust direction-search" >
					<Flex
						gap={8}
						align="center"
						wrap
					>
						{
							!isMobile &&
							<Flex style={{ flex: '1 0 100%' }}>
								<Button
									style={{ marginInlineStart: 'auto', width: 'fit-content', color: '#0088FF' }}
									type="link"
									icon={
										<FontAwesomeIcon
											icon={faXmark}
											fontSize={18}
										/>
									}
									onClick={onClose}
								/>
							</Flex>
						}

						<div className="direction-search-fields">
							<Form
								ref={ref}
								onChange={onChange}
								onKeyDown={onKeyDown}
								onKeyUp={onKeyUp}
								style={{ flex: 1 }}
							>
								<Item
									name="start"
									className="direction-point"
									label={
										<>
											<FontAwesomeIcon
												icon={faCircleDot}
												width={12}
											/>
											<FontAwesomeIcon
												className="direction-point-prefix"
												icon={faEllipsisV}
											/>
										</>
									}
								>
									<Select
										className={focusedField === 'start' && 'active'}
										onClick={(e) => onClick(e, 'start')}
										labelInValue
										optionFilterProp="search_key"
										notFoundContent={loading ? <Spin size="small" /> : null}
										options={startOptions}
										size="large"
										showSearch
										placeholder="Start"
										filterOption={customFilterOption}
										filterSort={hadleFilterSelect}
										optionRender={renderOption}
										onSelect={(data) => onSelect(data, 'start')}
										suffixIcon={null}
									/>
								</Item>
								{/* <Item
									name="_start"
									className="direction-point"
									label={
										<>
											<FontAwesomeIcon
												icon={faCircleDot}
												width={12}
											/>
											<FontAwesomeIcon
												className="direction-point-prefix"
												icon={faEllipsisV}
											/>
										</>
									}
									layout="horizontal"
								>
									<Input
										size="large"
										placeholder="From"
										autoComplete="off"
									/>
								</Item> */}
								{isMobile && <Divider className="divider" />}
								{/* <Item
									name="end"
									className="direction-point"
									label={
										<FontAwesomeIcon
											icon={faMapMarkerAlt}
											width={12}
											color={'#FF4D4F'}
										/>
									}
									layout="horizontal"
								>
									<Input
										size="large"
										placeholder="To"
										autoComplete="off"
									/>
								</Item> */}

								
								<Item
									name="end"
									className="direction-point"
									label={<FontAwesomeIcon
										icon={faMapMarkerAlt}
										color={'#FF4D4F'}
									       />}
									style={{ flex: '0 0 90%' }}
								>
									<Select
										className={focusedField === 'end' && 'active'}
										onClick={(e) => onClick(e, 'end')}
										labelInValue
										optionFilterProp="search_key"
										notFoundContent={loading ? <Spin size="small" /> : null}
										options={endOptions}
										size="large"
										showSearch
										placeholder="End"
										filterOption={customFilterOption}
										filterSort={hadleFilterSelect}
										optionRender={renderOption}
										onSelect={(data) => onSelect(data, 'end')}
										suffixIcon={null}
									/>
								</Item>

								{/* <List
								name="end"
								initialValue={['']}
							>
								{(fields, { add, remove }, { errors }) => (
									<>
										{fields.map((field, index, arr) => (
											<Item
												key={index}
												name={[field.name]}
												className="direction-point"
												layout="horizontal"
												label={
													<>
														<FontAwesomeIcon
															icon={index >= arr.length - 1 ? faMapMarkerAlt : faCircleDot}
															width={12}
															color={index >= arr.length - 1 ? '#FF4D4F' : undefined}
														/>
														{
															index < arr.length - 1 ?
																<FontAwesomeIcon
																	className="direction-point-prefix"
																	icon={faEllipsisV}
																/> : null
														}
													</>
												}
												style={{ flex: 1, marginBlockEnd: 0, marginBlockStart: index > 0 ? 8 : 0 }}
											>
												<Input
													size="large"
													suffix={
														<>
															<Button
																type="link"
																style={{ margin: 0 }}
																icon={
																	<FontAwesomeIcon
																		icon={faClose}
																		fontSize={20}
																		fontWeight="normal"
																	/>
																}
															/>
														</>
													}
												/>
											</Item>
										))}
										<Button
											onClick={() => {
												setIsMorethanOneDestination(true);
												add();
											}}
											style={{ marginBlockStart: 8, marginBlockEnd: 0, marginInlineStart: -4, padding: 0 }}
											type="link"
											icon={<PlusCircleOutlined style={{ fontSize: 18, verticalAlign: 'sub' }} />}
										>
											Add destination
										</Button>
									</>
								)}
							</List> */}
							</Form>
							<Button
								type="link"
								style={{ margin: 0, width: 'fit-content', marginBlockEnd: 0 }}
								onClick={handleOnSwap}
								icon={
									<SwapOutlined
										style={{ fontSize: 16 }}
										rotate={90}
									/>
								}
							/>
						</div>

						{/* style={{ margin: 0, width: 'fit-content', marginBlockEnd: 40 }} */}
					</Flex>
				</div>
			</>
		);
	}
);

export default MapDirectionsForm;